import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { MdCloudUpload, MdClose, MdCheck, MdPersonOutline } from 'react-icons/md';
import { setUpdateUser } from '../state';

const ProfilePhotoDialog = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    } else if (file) {
      setError('Please select an image file.');
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    } else if (file) {
      setError('Please select an image file.');
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a photo to upload');
      return;
    }

    setIsUploading(true);
    setError('');
    
    const formData = new FormData();
    
    const imageBlob = new Blob([selectedFile], { type: selectedFile.type });
    
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    const newFileName = `profile.${fileExtension}`;
    const modifiedFile = new File([imageBlob], newFileName, {
      type: selectedFile.type,
    });
    
    formData.append('photo', modifiedFile);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/update-profile-photo/${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined,
          'Api-Name': 'user-api',
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload photo');
      }

      const data = await response.json();
      
      // Update Redux store with new profile photo URL
      dispatch(setUpdateUser({ ...user, profile_photo_url: data.profile_photo_url }));
      
      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError(error.message || 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Skip for now
  const handleSkip = () => {
    onClose();
  };

  // Navigate to profile page
  const handleGoToProfile = () => {
    onClose();
    navigate('/profile');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-gradient-to-br from-pri-dark to-sec-dark rounded-2xl shadow-xl overflow-hidden w-full max-w-md"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
        >
          {/* Header */}
          <div className="p-5 border-b border-white/10 flex justify-between items-center">
            <h2 className="text-xl font-bold text-white">Complete Your Profile</h2>
            <button
              onClick={handleSkip}
              className="text-white/70 hover:text-white transition-colors"
            >
              <MdClose size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-5">
            <div className="text-center mb-6">
              <p className="text-white/80 mb-2">
                Adding a profile photo helps your friends recognize you and personalizes your experience.
              </p>
            </div>

            {/* Preview or Placeholder */}
            <div className="flex justify-center mb-6">
              {previewUrl ? (
                <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-soft-pink/50">
                  <img
                    src={previewUrl}
                    alt="Profile preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-soft-pink/30 to-mint-green/30 flex items-center justify-center border-4 border-white/10">
                  <MdPersonOutline size={64} className="text-white/60" />
                </div>
              )}
            </div>

            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-6 text-center mb-4 transition-colors ${
                isDragging
                  ? 'border-soft-pink bg-soft-pink/10'
                  : 'border-white/20 hover:border-soft-pink/50'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="photo-upload"
                className="cursor-pointer block"
              >
                <MdCloudUpload size={36} className="mx-auto mb-2 text-soft-pink" />
                <p className="text-white/80 mb-1">Drag & drop your photo here or</p>
                <button className="text-soft-pink hover:underline font-medium">
                  Browse Files
                </button>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/20 text-red-300 p-3 rounded-lg mb-4 text-sm">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSkip}
                className="flex-1 py-2.5 px-4 rounded-lg border border-white/20 text-white/70 hover:bg-white/10 transition-colors"
                disabled={isUploading}
              >
                Skip for Now
              </button>
              <button
                onClick={selectedFile ? handleUpload : handleGoToProfile}
                className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium hover:shadow-lg transition-all flex items-center justify-center"
                disabled={isUploading}
              >
                {isUploading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </div>
                ) : selectedFile ? (
                  <>
                    <MdCheck size={20} className="mr-2" />
                    Upload Photo
                  </>
                ) : (
                  "Go to Profile"
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfilePhotoDialog; 